import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import { toast } from "react-toastify";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [categories, setCategory] = useState([]);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    price: "",
    stock: "",
    category: "",
    avg: "",
    count: "",
    image: [], // image URLs for preview
    files: [], // image files to upload
  });

  useEffect(() => {
    fetchCategory();
    fetchProduct();
  }, [id]);

  const fetchCategory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://ecom-api.applligentdemo.com/category/getCategory",
        {
          headers: {
            Authorization: token,
          },
        }
      );
      setCategory(response.data.categories);
    } catch (error) {
      console.error("Error fetching category:", error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await fetch(
        `https://ecom-api.applligentdemo.com/product/getByProductId/${id}`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      const data = await response.json();
      const product = data.product;
      setFormData((prev) => ({
        ...prev,
        id: product._id || "",
        name: product.name || "",
        description: product.description || "",
        price: product.price || "",
        stock: product.stock || "",
        category: product.category || "",
        avg: product.averageRating || "",
        count: product.ratingCount || "",
        image: product.image || [],
      }));
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to fetch product details");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setFormData((prev) => ({
      ...prev,
      files,
      image: previews,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const form = new FormData();
      form.append("_id", formData.id);
      form.append("id", formData.id);
      form.append("name", formData.name);
      form.append("stock", formData.stock);
      form.append("description", formData.description);
      form.append("category", formData.category);
      form.append("averageRating", formData.avg);
      form.append("ratingCount", formData.count);

      formData.files.forEach((file) => {
        form.append("image", file);
      });

      // 🔍 Debug form data
      console.log("Sending form data:");
      for (let pair of form.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await fetch(
        `https://ecom-api.applligentdemo.com/product/updateProduct`,
        {
          method: "POST",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          body: form,
        }
      );

      const resText = await response.text();
      console.log("Server response:", resText);
      console.log("Product ID being sent:", formData.id);

      if (response.ok) {
        toast.success("Product updated successfully!");
        navigate("/product");
      } else {
        toast.error("Failed to update product");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Something went wrong");
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button variant="text" onClick={() => navigate(-1)}>
        ← Back
      </Button>

      <Paper
        elevation={4}
        sx={{ backgroundColor: "#EEEFE0", borderRadius: 4, padding: 4 }}
      >
        <Typography variant="h4" align="center" fontWeight={600} gutterBottom>
          Edit Product
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock"
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Average Rating"
                type="number"
                name="avg"
                value={formData.avg}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rating Count"
                type="number"
                name="count"
                value={formData.count}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: "#A7C1A8",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#93b29a" },
                }}
              >
                Choose Image
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {formData.image.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`preview-${index}`}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #ccc",
                    }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "#A7C1A8",
                  fontWeight: 600,
                  paddingY: 1.5,
                  "&:hover": { backgroundColor: "#93b29a" },
                }}
              >
                Update Product
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default EditProduct;
